// src/pages/SubHeadquarter/AddSubHeadquarter.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import subHeadquarterService from '../../services/subHeadquarter.service';
import headquarterService from '../../services/headquarter.service';
import regionService from '../../services/region.service';
import companyService from '../../services/company.service';
import '../../styles/AddPages.css';

const AddSubHeadquarter = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    companyId: '',
    regionId: '',
    headquarterId: '',
  });

  const [companies, setCompanies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [headquarters, setHeadquarters] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get admin ID for display
  const adminId = user?.employeeCode || user?.username || 'N/A';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCompanies();
    } else {
      // Set user's company and fetch regions
      const userCompanyId = user?.companyId || '';
      setFormData(prev => ({ ...prev, companyId: userCompanyId }));
      if (userCompanyId) {
        fetchRegions(userCompanyId);
      }
    }
  }, [isSuperAdmin, user]);

  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllCompanies();
      if (response.success && response.data) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchRegions = async (companyId) => {
    try {
      const response = await regionService.getAllRegions(companyId);
      if (response.success && response.data) {
        setRegions(response.data);
      } else {
        setRegions([]);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      setRegions([]);
    }
  };

  const fetchHeadquarters = async (regionId) => {
    try {
      const response = await headquarterService.getHeadquartersByRegion(regionId);
      if (response.success && response.data) {
        setHeadquarters(response.data);
      } else {
        setHeadquarters([]);
      }
    } catch (error) {
      console.error('Error fetching headquarters:', error);
      setHeadquarters([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Cascading dropdowns
    if (name === 'companyId' && value) {
      fetchRegions(value);
      setFormData(prev => ({ ...prev, regionId: '', headquarterId: '' })); // Reset
      setRegions([]);
      setHeadquarters([]);
    }
    
    if (name === 'regionId' && value) {
      fetchHeadquarters(value);
      setFormData(prev => ({ ...prev, headquarterId: '' })); // Reset HQ
      setHeadquarters([]);
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (isSuperAdmin && !formData.companyId) {
      newErrors.companyId = 'Company is required';
    }

    if (!formData.regionId) {
      newErrors.regionId = 'Region is required';
    }

    if (!formData.headquarterId) {
      newErrors.headquarterId = 'Headquarter is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Sub-Headquarter name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        regionId: parseInt(formData.regionId),
        headquarterId: parseInt(formData.headquarterId),
      };

      if (isSuperAdmin) {
        payload.companyId = parseInt(formData.companyId);
      }

      const response = await subHeadquarterService.createSubHeadquarter(payload);

      if (response.success) {
        alert('Sub-Headquarter added successfully!');
        navigate('/subhq/view');
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add sub-headquarter');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-subheadquarter-page">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/dashboard/overview">Dashboard</a>
          </li>
          <li className="breadcrumb-item">
            <a href="/subhq/view">Sub-Headquarters</a>
          </li>
          <li className="breadcrumb-item active">Add Sub-Headquarter</li>
        </ol>
      </nav>

      {/* Page Header */}
      <div className="page-header">
        <h4 className="page-title">Add New Sub-Headquarter</h4>
      </div>

      {/* Form Card */}
      <div className="form-card">
        <form onSubmit={handleSubmit}>
          {/* Admin Info */}
          <div className="form-section">
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Admin ID</label>
                <input
                  type="text"
                  className="form-input"
                  value={adminId}
                  readOnly
                  disabled
                  style={{ backgroundColor: '#f7fafc' }}
                />
              </div>
            </div>
          </div>

          {/* Sub-Headquarter Information */}
          <div className="form-section">
            <h3 className="section-title">Sub-Headquarter Information</h3>
            
            <div className="form-row">
              {isSuperAdmin && (
                <div className="form-group">
                  <label className="form-label">
                    Select Company <span className="required">*</span>
                  </label>
                  <select
                    name="companyId"
                    className={`form-input ${errors.companyId ? 'error' : ''}`}
                    value={formData.companyId}
                    onChange={handleChange}
                  >
                    <option value="">Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  {errors.companyId && (
                    <span className="error-message">{errors.companyId}</span>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  Select Region <span className="required">*</span>
                </label>
                <select
                  name="regionId"
                  className={`form-input ${errors.regionId ? 'error' : ''}`}
                  value={formData.regionId}
                  onChange={handleChange}
                  disabled={isSuperAdmin && !formData.companyId}
                >
                  <option value="">Select Region</option>
                  {regions.map(region => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </select>
                {errors.regionId && (
                  <span className="error-message">{errors.regionId}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Select Headquarter <span className="required">*</span>
                </label>
                <select
                  name="headquarterId"
                  className={`form-input ${errors.headquarterId ? 'error' : ''}`}
                  value={formData.headquarterId}
                  onChange={handleChange}
                  disabled={!formData.regionId}
                >
                  <option value="">Select Headquarter</option>
                  {headquarters.map(hq => (
                    <option key={hq.id} value={hq.id}>
                      {hq.name}
                    </option>
                  ))}
                </select>
                {errors.headquarterId && (
                  <span className="error-message">{errors.headquarterId}</span>
                )}
                {!formData.regionId && (
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Please select a region first
                  </small>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Sub-Headquarter Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter sub-headquarter name"
                />
                {errors.name && (
                  <span className="error-message">{errors.name}</span>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/subhq/view')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding Sub-Headquarter...' : 'Add Sub-Headquarter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSubHeadquarter;